import { useState, type FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import type { Location } from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';

type LocationFormProps = {
  organisationId: string;
  /** When set, saves via update instead of create. */
  location?: Location;
  onSaved: () => void;
  submitLabel?: string;
};

function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Create or edit a location — shared by Add location and Edit location flows.
 */
export function LocationForm({
  organisationId,
  location,
  onSaved,
  submitLabel,
}: LocationFormProps) {
  const { t } = useTranslation(['displays', 'common']);
  const isEdit = Boolean(location);
  const [name, setName] = useState(location?.name ?? '');
  const [timezone, setTimezone] = useState(location?.timezone ?? browserTimezone());
  const [address, setAddress] = useState(location?.address ?? '');

  const save = useMutation({
    mutationFn: async () => {
      const trimmedName = name.trim();
      const trimmedTimezone = timezone.trim() || 'UTC';
      const trimmedAddress = address.trim();
      if (location) {
        return api.updateLocation(organisationId, location.id, {
          name: trimmedName,
          timezone: trimmedTimezone,
          address: trimmedAddress.length > 0 ? trimmedAddress : null,
        });
      }
      return api.createLocation(organisationId, {
        name: trimmedName,
        ...(timezone.trim() ? { timezone: trimmedTimezone } : {}),
        ...(trimmedAddress ? { address: trimmedAddress } : {}),
      });
    },
    onSuccess: () => {
      if (!location) {
        setName('');
        setAddress('');
        setTimezone(browserTimezone());
      }
      onSaved();
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim()) return;
    save.mutate();
  };

  const label =
    submitLabel ?? (isEdit ? t('location.form.saveChanges') : t('location.form.add'));

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <label className="block text-sm font-medium text-ink">
        {t('location.form.name')}
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('location.form.namePlaceholder')}
          autoFocus
          className="mt-1.5 w-full rounded-lg border border-line bg-field px-3 py-2.5 text-sm text-ink outline-none focus:border-[var(--gs-accent)]"
          required
        />
      </label>
      <label className="block text-sm font-medium text-ink">
        {t('location.form.timezone')}
        <input
          value={timezone}
          onChange={(event) => setTimezone(event.target.value)}
          placeholder="America/New_York"
          className="mt-1.5 w-full rounded-lg border border-line bg-field px-3 py-2.5 text-sm text-ink outline-none focus:border-[var(--gs-accent)]"
          required={isEdit}
        />
        <span className="mt-1 block text-xs font-normal text-muted">
          {t('location.form.timezoneHint')}
        </span>
      </label>
      <label className="block text-sm font-medium text-ink">
        <Trans
          t={t}
          i18nKey="location.form.address"
          components={{ muted: <span className="font-normal text-muted" /> }}
        />
        <input
          value={address}
          onChange={(event) => setAddress(event.target.value)}
          placeholder={t('location.form.addressPlaceholder')}
          className="mt-1.5 w-full rounded-lg border border-line bg-field px-3 py-2.5 text-sm text-ink outline-none focus:border-[var(--gs-accent)]"
        />
      </label>
      {save.isError ? (
        <p className="m-0 text-sm text-bad" role="alert">
          {apiErrorMessage(
            save.error,
            isEdit ? t('location.form.saveError') : t('location.form.createError'),
          )}
        </p>
      ) : null}
      <button
        type="submit"
        disabled={save.isPending || !name.trim()}
        className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-[var(--gs-accent-ink)] disabled:opacity-60"
        style={{ background: 'var(--gs-accent-solid)' }}
      >
        {save.isPending
          ? isEdit
            ? t('common:actions.saving')
            : t('location.form.adding')
          : label}
      </button>
    </form>
  );
}
